import React from 'react';
import { useTranslation } from 'react-i18next';
import {useSelector} from '../../reduxHooks';
import { Route, Switch } from 'react-router-dom';
import { Card } from '@betareader/design-system';
import styled from 'styled-components';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { featureToggleUtil } from 'src/utils';
import { CenteredContent } from 'src/style';
import { MediaQuery } from 'src/constants/size';
import CritiqueGroupList from './critiqueGroupList';
import CreateCritiqueGroup from './createCritiqueGroup';
import CritiqueGroupDetail from './critiqueGroupDetail';
import CritiqueDiscover from './critiqueDiscover';

/**
 * Critique Groups route shell.
 *
 * The route is always registered (matching how the app gates AI Editor) and the
 * gate lives here, so a dark deployment renders "not available" rather than a
 * 404 — and, importantly, fires no requests: the API is dark too and would 403
 * every call.
 *
 * The gate is two-tier, mirroring the server's `isFeatureEnabled(key) || user
 * .betaFeatures[key]`. Checking only the global flag would leave beta-granted
 * users with an API that answers 200 and a UI that refuses to show it.
 */

// CenteredContent lives in an untyped .js module, so its `maxWidth` prop isn't
// visible to TS — restate it here rather than casting at each call site.
const Wrapper = styled(CenteredContent)<{ maxWidth?: number }>`
  padding: 24px 16px 60px;
  ${MediaQuery.phone} {
    /* This padding is added to the route's min-content width, so on a 320px
       phone the 16px inset was the difference between fitting and a 1px
       horizontal scroll. */
    padding: 18px 12px 48px;
  }
`;

export const useCritiqueGroupsEnabled = (): {
  enabled: boolean;
  isLoading: boolean;
} => {
  const { flags, isLoading } = useFeatureFlags();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const enabled =
    flags?.critique_groups === true ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'critique_groups');
  return { enabled, isLoading };
};

const CritiqueGroups: React.FC = () => {
  const { t } = useTranslation();
  const { enabled, isLoading } = useCritiqueGroupsEnabled();

  // Render nothing while the flag resolves — showing the fallback first would
  // flash "not available" at users who do have access.
  if (isLoading) return null;

  if (!enabled) {
    return (
      <Wrapper maxWidth={900}>
        <Card data-testid="critique-groups-unavailable">
          {t('FeatureNotAvailable', 'This feature is not available.')}
        </Card>
      </Wrapper>
    );
  }

  return (
    <Wrapper maxWidth={1100}>
      <Switch>
        <Route exact path="/critique-groups" component={CritiqueGroupList} />
        <Route exact path="/critique-groups/new" component={CreateCritiqueGroup} />
        <Route exact path="/critique-groups/discover" component={CritiqueDiscover} />
        <Route path="/critique-groups/:groupId" component={CritiqueGroupDetail} />
      </Switch>
    </Wrapper>
  );
};

export default CritiqueGroups;
